import { useMutation } from "@tanstack/react-query";
import { importSection } from "api-client";
import { Icon } from "icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui";

import { useMinimapDragAndDropManager } from "src/components/MinimapDragAndDropManager";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { SectionDialogEdit } from "src/sections/SectionDialog";

import { SectionSelectionTable } from "./SectionSelectionTable";
import { useWorkoutEditStore } from "./WorkoutEditContext";
import { scrollWorkoutItemIntoView } from "./workoutItemScroll";
import { useWorkoutItemsDragAndDropManager } from "./WorkoutItemsDragAndDropManagerContext";

interface ImportSectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insertAt: number;
}

export function ImportSectionDialog(props: ImportSectionDialogProps) {
  const { t } = useTranslation();
  const workoutEditStore = useWorkoutEditStore();
  const dndManager = useWorkoutItemsDragAndDropManager();
  const minimapManager = useMinimapDragAndDropManager();
  const { withSaving } = useAuthenticatedNavigationContext();
  const [previewSectionId, setPreviewSectionId] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      return withSaving(
        importSection({
          path: { workoutId: workoutEditStore.workout.id },
          body: { sectionId, at: props.insertAt },
        }),
      );
    },
    onSuccess: (res) => {
      const operations = res.data.data.operations;
      workoutEditStore.applyWorkoutItemChanges(operations);

      const newGroupIds = operations.flatMap((op) => {
        return op.operation === "create" && op.data.type === "GROUP"
          ? [op.data.id]
          : [];
      });
      const newWorkoutItemIds = operations.flatMap((op) => {
        return op.operation === "create" ? [op.data.id] : [];
      });
      const firstNewWorkoutItemId =
        newGroupIds[0] ?? newWorkoutItemIds[0] ?? null;

      dndManager.openAccordionItems(newGroupIds);

      for (const id of newGroupIds) {
        minimapManager.expandItem(id);
      }

      setPreviewSectionId(null);
      props.onOpenChange(false);

      if (firstNewWorkoutItemId !== null) {
        void scrollWorkoutItemIntoView({
          workoutItemId: firstNewWorkoutItemId,
        });
      }
    },
  });

  const importingSectionId = importMutation.isPending
    ? (importMutation.variables ?? null)
    : null;

  return (
    <>
      <Dialog open={props.open} onOpenChange={props.onOpenChange}>
        <DialogContent className="min-w-[650px] w-full max-h-[90svh] h-content flex flex-col">
          <DialogHeader>
            <DialogTitle>{t("editWorkout.importSection.title")}</DialogTitle>
            <DialogDescription>
              {t("editWorkout.importSection.description")}
            </DialogDescription>
          </DialogHeader>

          <SectionSelectionTable
            onPreview={setPreviewSectionId}
            onImport={(sectionId) => {
              importMutation.mutate(sectionId);
            }}
            importingSectionId={importingSectionId}
          />

          <DialogFooter className="flex items-center justify-end">
            <Button
              variant="outline"
              onClick={() => {
                props.onOpenChange(false);
              }}
            >
              {t("editWorkout.importSection.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {previewSectionId && (
        <SectionDialogEdit
          sectionId={previewSectionId}
          open={true}
          disabled
          onOpenChange={(open) => {
            if (!open) {
              setPreviewSectionId(null);
            }
          }}
          extraAction={
            <Button
              disabled={importMutation.isPending}
              onClick={() => {
                importMutation.mutate(previewSectionId);
              }}
            >
              {importMutation.isPending && (
                <Icon name="tabler-loader-2" className="h-4 w-4 animate-spin" />
              )}
              {t("editWorkout.importSection.import")}
            </Button>
          }
        />
      )}
    </>
  );
}
